import useForm from '@/hooks/useForm/useForm';
import ButtonSubmit from '@uikit/components/ButtonSubmit/ButtonSubmit';
import Form from '@uikit/components/Form/Form';
import FormField from '@uikit/components/FormField/FormField';
import FormInfoBox from '@uikit/components/FormInfoBox/FormInfoBox';
import FormRow from '@uikit/components/FormRow/FormRow';
import CheckboxWithLabel from '../CheckboxWithLabel/CheckboxWithLabel';
import { FormFieldType } from '../FormField/consts';
import Headline from '../Headline/Headline';
import { HeadlineColor } from '../Headline/consts';
import Spacer from '../Spacer/Spacer';
import schema from './schema';
import type { CourseRequestFormProps, CourseRequestFormState } from './types';

const initialFormState: CourseRequestFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  memberAmount: undefined,
  desiredDate: '',
  marketingAgreement: false,
  privacyAgreement: false,
};

const CourseRequestForm = ({
  onSubmit,
  submitButtonStatus,
  formName,
}: CourseRequestFormProps): JSX.Element => {
  const {
    formState,
    getErrorByFieldName,
    isFormValid,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useForm(onSubmit, schema, initialFormState, 'course-request-form');

  return (
    <Form onSubmit={handleSubmit} name={formName}>
      <Headline
        headlineColor={HeadlineColor.Gradient}
        subText="Bitte nennen Sie uns Ihren Wunschtermin und die Anzahl der Schulungsteilnehmer:innen. Wir melden uns bei Ihnen."
      >
        Individuelle Anfrage
      </Headline>

      <Spacer defaultSize={2} />

      <FormRow>
        <FormField
          autoComplete="given-name"
          errorMessage={getErrorByFieldName('firstName')}
          id="first-name"
          label="Ihr Vorname"
          name="firstName"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState.firstName}
        />
        <FormField
          autoComplete="family-name"
          errorMessage={getErrorByFieldName('lastName')}
          id="last-name"
          label="Ihr Nachname"
          name="lastName"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState.lastName}
        />
      </FormRow>

      <FormRow>
        <FormField
          autoComplete="email"
          errorMessage={getErrorByFieldName('email')}
          id="email"
          label="E-Mail-Adresse"
          name="email"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState.email}
        />
        <FormField
          autoComplete="tel"
          errorMessage={getErrorByFieldName('phone')}
          id="phone"
          label="Telefonnummer"
          name="phone"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState.phone}
        />
      </FormRow>

      <FormRow>
        <FormField
          errorMessage={getErrorByFieldName('memberAmount')}
          id="memberAmount"
          label="Personenanzahl"
          name="memberAmount"
          type={FormFieldType.Number}
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState.memberAmount}
        />
        <FormField
          errorMessage={getErrorByFieldName('desiredDate')}
          id="desiredDate"
          label="Wunschtermin"
          type={FormFieldType.Date}
          name="desiredDate"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState.desiredDate}
        />
      </FormRow>

      <FormRow>
        <CheckboxWithLabel
          label="Ich bin damit einverstanden, aktuelle Angebote und Informationen zu Schulungen, Netzdienstleistungen und Services der enercity AG per E-Mail zu erhalten. (optional)"
          id="marketingAgreement"
          name="marketingAgreement"
          checked={formState.marketingAgreement}
          onChange={handleChange}
          errorMessage={getErrorByFieldName('marketingAgreement')}
        />
      </FormRow>

      <FormRow>
        <CheckboxWithLabel
          label="Hiermit willige ich ein, dass die von mir angegebenen personenbezogenen Daten gemäß der Datenschutzerklärung verarbeitet werden."
          id="privacyAgreement"
          name="privacyAgreement"
          checked={formState.privacyAgreement}
          onChange={handleChange}
          errorMessage={getErrorByFieldName('privacyAgreement')}
        />
      </FormRow>

      <FormRow>
        <FormInfoBox>
          Informationen gemäß Art. 13 DSGVO über die Verarbeitung Ihrer
          personenbezogenen Daten finden Sie in unseren
          Datenschutzinformationen.
        </FormInfoBox>
      </FormRow>

      <FormRow fullWidthChildren={false}>
        <ButtonSubmit
          id="course-contact-form-submit-button"
          inactive={!isFormValid}
          state={submitButtonStatus}
        >
          Absenden
        </ButtonSubmit>
      </FormRow>
    </Form>
  );
};

export default CourseRequestForm;
